/**
 * Nexera Design System - Color Palette
 *
 * This file is the SINGLE SOURCE OF TRUTH for all colors in the application.
 * To change the color scheme, simply modify the values in `palette` below.
 *
 * Current Theme: Coastal Warmth
 * Inspired by Mediterranean beaches - warm, inviting, and fresh.
 */

// =============================================================================
// EDITABLE COLOR PALETTE
// Change these values to update the entire application's color scheme
// =============================================================================

export const palette = {
  // Primary brand color - used for main actions, active states, links
  primary: '#0FA3B1',        // Teal - the hero color
  primaryLight: '#3DB8C4',   // Lighter teal for hover states
  primaryDark: '#0C8A96',    // Darker teal for pressed states

  // Secondary accent - used for highlights, badges, special CTAs
  accent: '#F7A072',         // Coral/Peach - warm and energetic
  accentLight: '#F9B896',    // Lighter coral
  accentDark: '#E8895A',     // Darker coral

  // Tertiary colors - supporting accents
  tertiary: '#EDDEA4',       // Sand Yellow - warm, subtle
  tertiaryLight: '#F5EBCA',  // Lighter sand

  // Sky color - for soft backgrounds, cards, highlights
  sky: '#B5E2FA',            // Light Sky Blue - airy and fresh
  skyLight: '#D4EEFB',       // Even lighter sky

  // Neutral backgrounds
  background: '#F9F7F3',     // Cream/Off-White - warm neutral
  surface: '#FFFFFF',        // Pure white for cards
  surfaceHover: '#F5F3EF',   // Slightly darker for hover

  // Text colors
  textPrimary: '#2D3436',    // Dark charcoal - excellent readability
  textSecondary: '#636E72',  // Medium gray
  textMuted: '#9BA3A9',      // Light gray for hints
  textOnPrimary: '#FFFFFF',  // White text on primary color
  textOnAccent: '#FFFFFF',   // White text on accent color

  // Borders and dividers
  border: '#E8E4DE',         // Warm gray border
  borderLight: '#F0ECE6',    // Very light border

  // Semantic colors
  success: '#00B894',        // Fresh green
  successLight: '#E8F8F5',
  warning: '#FDCB6E',        // Warm yellow
  warningLight: '#FEF9E7',
  error: '#E17055',          // Soft red-orange (matches palette warmth)
  errorLight: '#FDECEA',

  // Sidebar (dark variant)
  sidebarBg: '#2D3436',      // Dark charcoal
  sidebarHover: '#3D4446',   // Lighter for hover
  sidebarText: '#B2BEC3',    // Muted text
  sidebarActive: '#0FA3B1',  // Primary teal for active
};

// =============================================================================
// DERIVED THEME OBJECT
// These are computed values used throughout the app - generally don't edit these
// =============================================================================

export const theme = {
  colors: palette,

  // Gradients
  gradients: {
    primary: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
    accent: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accentDark} 100%)`,
    hero: `linear-gradient(135deg, ${palette.sidebarBg} 0%, #3D4446 100%)`,
    sky: `linear-gradient(135deg, ${palette.sky} 0%, ${palette.skyLight} 100%)`,
    warm: `linear-gradient(135deg, ${palette.tertiary} 0%, ${palette.accent} 100%)`,
  },

  // Shadows with brand color tint
  shadows: {
    sm: '0px 2px 8px rgba(45, 52, 54, 0.06)',
    md: '0px 4px 16px rgba(45, 52, 54, 0.08)',
    lg: '0px 8px 32px rgba(45, 52, 54, 0.10)',
    xl: '0px 16px 48px rgba(45, 52, 54, 0.12)',
    primary: `0px 4px 16px ${palette.primary}30`,
    accent: `0px 4px 16px ${palette.accent}30`,
  },

  // Border radius
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },

  // Transitions
  transitions: {
    fast: '0.15s ease',
    normal: '0.25s ease',
    slow: '0.4s ease',
    bounce: '0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Add opacity to a hex color
 * @param hex - Hex color string (e.g., '#0FA3B1')
 * @param opacity - Opacity value from 0 to 1
 * @returns RGBA color string
 */
export const withOpacity = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get contrasting text color (black or white) for a background
 * @param hex - Background hex color
 * @returns '#FFFFFF' or '#2D3436'
 */
export const getContrastText = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#2D3436' : '#FFFFFF';
};

export default theme;
